/**
 * category.js
 */

import React from 'react';
import { useDrop } from 'react-dnd';
import { NativeTypes } from 'react-dnd-html5-backend';
import makeStyles from '@mui/styles/makeStyles';
import {
  Typography,
  Box,
  IconButton,
  Table,
  TableBody,
  TableContainer,
} from '@mui/material';
import { ReactSVG } from 'react-svg'
import Icon from 'components/icon';
import Button from 'components/button';
import emptySVG from 'assets/images/empty.svg';
import { itemTypes } from './models';
import Document, { DocumentDisplay } from './document';


const useStyles = makeStyles(theme => ({
  dropContainer: {
    transition: `${theme.transitions.duration.short}ms ${theme.transitions.easing.easeOut}`,
    outline: 'none',
    border: '4px solid transparent',
    borderRadius: '5px',
    '& .emptySVG svg': {
      display: 'block',
      width: '25%',
      [theme.breakpoints.down('sm')]: {
        width: '40%',
      },
      maxWidth: theme.spacing(40),
      height: 'auto',
      margin: `${theme.spacing(4)} auto ${theme.spacing(2)}`,
      opacity: 0.5,
      '& .custom-fill': {
        fill: theme.palette.primary.colors[1],
      },
    },
  },
  dragOver: {
    background: theme.palette.grey.colors[10],
    border: `dashed 4px ${theme.palette.grey.colors[7]}`,
    cursor: 'grab',
  },
  table: {
    '& .MuiTableRow-hover': {
      cursor: 'pointer',
    },
    '& .MuiTableRow-root:hover': {
      background: theme.palette.grey.colors[9],
    },
  },
  renameCategoryButton: {
    '&:hover $renameCategoryIcon': {
      opacity: 1
    }
  },
  renameCategoryIcon: {
    transition: `${theme.transitions.duration.standard}ms ${theme.transitions.easing.easeOut}`,
    opacity: 0,
  },
  categoryText: {
    lineHeight: 1.2,
    textAlign: 'left',
    textTransform: 'uppercase',
  },
  noFiles: {
    textAlign: 'center',
  },
}));


export function CategoryTitle(props) {
  const { title } = props;
  const classes = useStyles();

  return (
    <Typography className={classes.categoryText} variant="h6">{title}</Typography>
  );
}


export function FileList(props) {
  const { 
    categoryName, 
    files, 
    category, 
    editFilename, 
    deleteFile,
    draggable,
  } = props;
  const classes = useStyles();

  const DocumentComponent = draggable ? Document : DocumentDisplay;

  return (
    files.length > 0
      ? <TableContainer>
        <Table
          className={classes.table}
          aria-label={`Documents for ${categoryName} category`}
          size="small"
        >
          <TableBody>
            {files.map(file => (
              <DocumentComponent
                key={file.localId}
                file={file}
                category={category}
                editFilename={editFilename}
                deleteFile={deleteFile}
              />
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      : <div className={classes.noFiles}>
        <ReactSVG src={emptySVG} className="emptySVG" />
        <Typography variant="body2">
          No files found
        </Typography>
      </div>
  );
}


export default function Category(props) {
  const {
    onDrop,
    category,
    documents,
    onFilesAdded,
    editCategory,
    deleteCategory,
    editFilename,
    deleteFile,
  } = props;
  const classes = useStyles();
  const fileInput = React.useRef();
  const [{ isOver, canDrop }, drop] = useDrop({
    accept: [itemTypes.FILE, NativeTypes.FILE],
    drop: onDrop,
    canDrop: (item, monitor) => {
      const type = monitor.getItemType();
      return type && type === itemTypes.FILE
        ? item.file.category.localId !== category.localId
        : true;
    },
    collect: (monitor) => ({
      isOver: monitor.isOver(),
      canDrop: monitor.canDrop(),
    }),
  });


  const onFilesChanged = () => {
    onFilesAdded([...fileInput.current.files], category);
    fileInput.current.value = '';
  }


  return (
    <div style={{ flex: 1 }} ref={drop}>
      <input
        ref={fileInput}
        type="file"
        multiple
        onChange={onFilesChanged}
        style={{ display: 'none' }}
      />
      <Box
        flex={1}
        my={3}
        className={`
           ${classes.dropContainer}
           ${canDrop && isOver ? classes.dragOver : ''}
         `}
      >
        {/* category header / title */}
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
          <Box display="flex" alignItems="center" mr={4}>
            <Button
              size='small'
              className={classes.renameCategoryButton}
              endIcon={<Icon className={classes.renameCategoryIcon} type='edit' fontSize="small" />}
              onClick={() => editCategory(category)}
              title="Rename this category"
            >
              <CategoryTitle title={category.name} />
            </Button>
            {(category.files.length === 0 && documents.length > 1) &&
              <IconButton
                size='small'
                onClick={() => deleteCategory(category)}
                disabled={category.files.length > 0 || documents.length === 1}
              >
                <Icon type='delete' fontSize="small" />
              </IconButton>}
          </Box>
          <Box display="flex">
            <Button
              color="primary"
              aria-label="add files"
              size="small"
              onClick={() => fileInput.current.click()}
              title="Click here to select files to add to this category&#013;&#010;or drag and drop files directly from your computer"
            >
              ADD&nbsp;FILES
            </Button>
          </Box>
        </Box>

        {/* files list */}
        <FileList
          files={category.files}
          categoryName={category.name}
          category={category}
          editFilename={editFilename}
          deleteFile={deleteFile}
          draggable
        />
      </Box>

    </div>
  )
}